import { useState, useEffect } from 'react';

interface AnimatedHeadingProps {
  text: string;
  className?: string;
  delay?: number;
  charDelay?: number;
  animate?: boolean;
  align?: 'left' | 'center';
}

export default function AnimatedHeading({
  text,
  className = '',
  delay = 200,
  charDelay = 30,
  animate,
  align = 'center',
}: AnimatedHeadingProps) {
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (animate !== undefined) {
      setStarted(animate);
      return;
    }
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [delay, animate]);

  const lines = text.split('\n');

  return (
    <div className={className} style={{ letterSpacing: '-0.04em' }}>
      {lines.map((line, lineIndex) => {
        const groups: { type: 'word' | 'space'; chars: string[] }[] = [];
        let cur: string[] = [];
        let curType: 'word' | 'space' | null = null;
        for (const ch of line) {
          const t = ch === ' ' ? 'space' : 'word';
          if (t !== curType) {
            if (cur.length) groups.push({ type: curType!, chars: cur });
            cur = [];
            curType = t;
          }
          cur.push(ch);
        }
        if (cur.length) groups.push({ type: curType!, chars: cur });

        let precedingCount = 0;

        return (
          <div key={lineIndex} className={`flex flex-wrap ${align === 'center' ? 'justify-center' : 'justify-start'}`}>
            {groups.map((group, gi) => {
              const node = (
                <span
                  key={gi}
                  className={
                    group.type === 'word'
                      ? 'inline-block whitespace-nowrap'
                      : 'inline-block'
                  }
                >
                  {group.chars.map((char, ci) => {
                    const charIndex = precedingCount + ci;
                    const transitionDelay =
                      lineIndex * line.length * charDelay + charIndex * charDelay;
                    return (
                      <span
                        key={ci}
                        className="inline-block transition-all duration-500"
                        style={{
                          opacity: started ? 1 : 0,
                          transform: started ? 'translateX(0)' : 'translateX(-18px)',
                          transitionDelay: `${transitionDelay}ms`,
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    );
                  })}
                </span>
              );
              precedingCount += group.chars.length;
              return node;
            })}
          </div>
        );
      })}
    </div>
  );
}